// Join game view - accept game invitation and select character

import { css, html } from 'lit';
import { api, getCDNBaseUrl } from '../../lib/api.js';
import { BaseComponent } from '../../lib/base-component.js';
import { router } from '../../lib/router.js';

// ArrowLeft icon SVG
const arrowLeftIcon = html`<svg
  xmlns="http://www.w3.org/2000/svg"
  width="18"
  height="18"
  viewBox="0 0 24 24"
  fill="none"
  stroke="currentColor"
  stroke-width="2"
  stroke-linecap="round"
  stroke-linejoin="round"
  class="me-1"
>
  <path d="m12 19-7-7 7-7" />
  <path d="M19 12H5" />
</svg>`;

// Mail icon SVG
const mailIcon = html`<svg
  xmlns="http://www.w3.org/2000/svg"
  width="24"
  height="24"
  viewBox="0 0 24 24"
  fill="none"
  stroke="currentColor"
  stroke-width="2"
  stroke-linecap="round"
  stroke-linejoin="round"
  class="me-2"
>
  <rect width="20" height="16" x="2" y="4" rx="2" />
  <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" />
</svg>`;

// SquareUserRound icon SVG
const userIconMedium = html`<svg
  xmlns="http://www.w3.org/2000/svg"
  width="32"
  height="32"
  viewBox="0 0 24 24"
  fill="none"
  stroke="currentColor"
  stroke-width="1.5"
  stroke-linecap="round"
  stroke-linejoin="round"
>
  <path d="M18 21a6 6 0 0 0-12 0" />
  <circle cx="12" cy="11" r="4" />
  <rect width="18" height="18" x="3" y="3" rx="2" />
</svg>`;

/**
 * Join game view component
 * Allows accepting a game invitation and selecting a character to join
 */
class JoinGameView extends BaseComponent {
  static properties = {
    gameId: { type: String, attribute: 'game-id' },
    _invitation: { type: Object, state: true },
    _loading: { type: Boolean, state: true },
    _error: { type: String, state: true },
    _characters: { type: Array, state: true },
    _charactersLoading: { type: Boolean, state: true },
    _selectedCharacterId: { type: String, state: true },
    _joining: { type: Boolean, state: true },
    _joinError: { type: String, state: true },
  };

  static styles = css`
    :host {
      display: block;
      overflow-y: auto;
    }

    .character-card {
      display: flex;
      align-items: center;
      gap: 12px;
      padding: 12px;
      border: 2px solid #dee2e6;
      border-radius: 8px;
      cursor: pointer;
      transition:
        border-color 0.15s,
        box-shadow 0.15s;
      min-width: 200px;
    }

    .character-card:hover:not(.disabled) {
      border-color: #0d6efd;
    }

    .character-card.disabled {
      opacity: 0.5;
      cursor: not-allowed;
      border-color: #dee2e6;
    }

    .in-game-badge {
      font-size: 0.7rem;
      padding: 2px 6px;
      border-radius: 4px;
      background-color: #6c757d;
      color: white;
      white-space: nowrap;
    }

    .character-card.selected {
      border-color: #0d6efd;
      box-shadow: 0 0 0 3px rgba(13, 110, 253, 0.25);
    }

    .character-image {
      width: 64px;
      height: 64px;
      border-radius: 8px;
      overflow: hidden;
      flex-shrink: 0;
    }

    .character-image img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }

    .character-placeholder {
      width: 100%;
      height: 100%;
      background: #6c757d;
      display: flex;
      align-items: center;
      justify-content: center;
      color: white;
    }

    .character-info {
      flex-grow: 1;
      min-width: 0;
    }

    .character-name {
      font-weight: 600;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    .character-details {
      font-size: 0.875rem;
      color: #6c757d;
    }
  `;

  constructor() {
    super();
    this.gameId = '';
    /** @type {{gameId: string, inviterEmailHash: string, inviterUsername: string, gameAdventureName: string, createdAt: string}|null} */
    this._invitation = null;
    this._loading = true;
    this._error = null;
    /** @type {Array<{id: string, name: string, level?: number, class?: string, tokenId?: string, activeGameId?: string}>} */
    this._characters = [];
    this._charactersLoading = true;
    this._selectedCharacterId = null;
    this._joining = false;
    this._joinError = null;
  }

  async connectedCallback() {
    await super.connectedCallback();
    Promise.all([this._loadInvitation(), this._loadCharacters()]);
  }

  async _loadInvitation() {
    this._loading = true;
    this._error = null;

    try {
      const invitations =
        /** @type {Array<{gameId: string, inviterEmailHash: string, inviterUsername: string, gameAdventureName: string, createdAt: string}>} */ (
          await api.getMyInvitations()
        );
      const found = invitations.find((/** @type {{gameId: string}} */ inv) => inv.gameId === this.gameId);

      if (!found) {
        this._error = 'Invitation not found or already accepted';
        return;
      }

      this._invitation = found;
    } catch (err) {
      console.error('Failed to load invitation:', err);
      this._error = 'Failed to load invitation. Please try again.';
    } finally {
      this._loading = false;
    }
  }

  async _loadCharacters() {
    this._charactersLoading = true;

    try {
      this._characters =
        /** @type {Array<{id: string, name: string, level?: number, class?: string, tokenId?: string, activeGameId?: string}>} */ (
          await api.getCharacters()
        );
      // Auto-select: prefer query param (from character creation returnTo),
      // then fall back to single available character.
      const querySelected = router.getQuery().get('selectedCharacterId');
      const available = this._characters.filter((c) => !c.activeGameId);
      if (querySelected && available.some((c) => c.id === querySelected)) {
        this._selectedCharacterId = querySelected;
      } else if (available.length === 1 && available[0]) {
        this._selectedCharacterId = available[0].id;
      }
    } catch (err) {
      console.error('Failed to load characters:', err);
    } finally {
      this._charactersLoading = false;
    }
  }

  /** @param {{tokenId?: string}} character */
  _getCharacterTokenImage(character) {
    if (!character.tokenId || !getCDNBaseUrl()) return null;
    return `${getCDNBaseUrl()}/token/${character.tokenId}`;
  }

  /** @param {string} emailHash */
  _getGravatarUrl(emailHash) {
    return `https://www.gravatar.com/avatar/${emailHash}?d=identicon&s=96`;
  }

  /** @param {string} dateString */
  _formatDate(dateString) {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'just now';
    if (diffMins < 60) return `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
    return date.toLocaleDateString();
  }

  /** @param {{id: string, activeGameId?: string}} character */
  _selectCharacter(character) {
    if (character.activeGameId) return;
    this._selectedCharacterId = character.id;
  }

  async _joinGame() {
    if (!this._selectedCharacterId || !this._invitation) return;

    this._joining = true;
    this._joinError = null;

    try {
      await api.joinGame(this._invitation.gameId, this._selectedCharacterId);
      router.navigate(`/game/${this._invitation.gameId}`);
    } catch (err) {
      console.error('Failed to join game:', err);
      this._joinError = /** @type {Error} */ (err).message || 'Failed to join game. Please try again.';
    } finally {
      this._joining = false;
    }
  }

  _goToGames() {
    router.navigate('/games');
  }

  _goToCharacters() {
    const returnTo = encodeURIComponent(`/game/${this.gameId}/join`);
    router.navigate(`/characters/new?skipRestore=true&returnTo=${returnTo}`);
  }

  render() {
    return html`
      <div class="container-fluid p-4">
        ${
          this._loading
            ? html`
              <div class="text-center py-5">
                <div class="spinner-border" role="status">
                  <span class="visually-hidden">Loading...</span>
                </div>
                <p class="mt-3 text-muted">Loading invitation...</p>
              </div>
            `
            : this._error
              ? html`
                <div class="alert alert-danger" role="alert">
                  ${this._error}
                  <button class="btn btn-outline-secondary ms-3" @click="${this._goToGames}">Back to My Games</button>
                </div>
              `
              : this._invitation
                ? html`
                  <div class="row justify-content-center">
                    <div class="col-lg-6">
                      <button class="btn btn-outline-secondary mb-3" @click="${this._goToGames}">
                        ${arrowLeftIcon} Back to My Games
                      </button>

                      <!-- Invitation Card -->
                      <div class="card mb-4">
                        <div
                          class="card-header d-flex align-items-center"
                          style="background: linear-gradient(135deg, #0d6efd 0%, #6610f2 100%); color: white;"
                        >
                          ${mailIcon}
                          <span class="fw-bold">Game Invitation</span>
                        </div>
                        <div class="card-body">
                          <div class="d-flex align-items-center mb-3">
                            <img
                              src="${this._getGravatarUrl(this._invitation.inviterEmailHash)}"
                              alt="Inviter"
                              class="rounded-circle me-3"
                              style="width: 48px; height: 48px;"
                            />
                            <div>
                              <p class="mb-0">
                                <strong>${this._invitation.inviterUsername}</strong> has invited you to join:
                              </p>
                              <h5 class="mb-0 mt-1">${this._invitation.gameAdventureName}</h5>
                            </div>
                          </div>
                          <p class="text-muted small mb-0">Invited ${this._formatDate(this._invitation.createdAt)}</p>
                        </div>
                      </div>

                      <!-- Character Selection -->
                      <h5 class="mb-3">Select Your Character</h5>

                      ${
                        this._charactersLoading
                          ? html`
                            <div class="text-center py-4">
                              <div class="spinner-border spinner-border-sm" role="status">
                                <span class="visually-hidden">Loading characters...</span>
                              </div>
                              <p class="mt-2 text-muted small">Loading your characters...</p>
                            </div>
                          `
                          : this._characters.length === 0
                            ? html`
                              <div class="alert alert-warning">
                                <p class="mb-2">You don't have any characters yet.</p>
                                <button class="btn btn-primary btn-sm" @click="${this._goToCharacters}">
                                  Create a Character
                                </button>
                              </div>
                            `
                            : html`
                              <div class="d-flex flex-wrap gap-3 mb-4">
                                <div
                                  class="character-card"
                                  @click="${this._goToCharacters}"
                                >
                                  <div class="character-image">
                                    <div class="character-placeholder" style="font-size:2rem;color:#0d6efd;background:rgba(13,110,253,0.1);border:2px dashed #0d6efd;border-radius:8px;">+</div>
                                  </div>
                                  <div class="character-info">
                                    <div class="character-name">New Character</div>
                                    <div class="character-details">Create a new character</div>
                                  </div>
                                </div>
                                ${this._characters.map(
                                  (character) => html`
                                    <div
                                      class="character-card ${
                                        this._selectedCharacterId === character.id ? 'selected' : ''
                                      } ${character.activeGameId ? 'disabled' : ''}"
                                      @click="${() => this._selectCharacter(character)}"
                                    >
                                      <div class="character-image">
                                        ${
                                          this._getCharacterTokenImage(character)
                                            ? html` <img src="${this._getCharacterTokenImage(character)}" alt="Token" /> `
                                            : html` <div class="character-placeholder">${userIconMedium}</div> `
                                        }
                                      </div>
                                      <div class="character-info">
                                        <div class="character-name">${character.name}</div>
                                        <div class="character-details">Level ${character.level} ${character.class}</div>
                                        ${
                                          character.activeGameId
                                            ? html` <span class="in-game-badge">In Active Game</span> `
                                            : ''
                                        }
                                      </div>
                                    </div>
                                  `,
                                )}
                              </div>
                            `
                      }

                      <!-- Join button -->
                      ${
                        this._characters.length > 0
                          ? html`
                            <div class="d-flex gap-2">
                              <button
                                class="btn btn-primary btn-lg"
                                ?disabled="${!this._selectedCharacterId || this._joining}"
                                @click="${this._joinGame}"
                              >
                                ${
                                  this._joining ? html`<span class="spinner-border spinner-border-sm me-2"></span>` : ''
                                }
                                ${this._joining ? 'Joining...' : 'Join Game'}
                              </button>
                            </div>
                          `
                          : ''
                      }
                      ${this._joinError ? html` <div class="alert alert-danger mt-3">${this._joinError}</div> ` : ''}
                    </div>
                  </div>
                `
                : ''
        }
      </div>
    `;
  }
}

customElements.define('join-game-view', JoinGameView);
